import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ReadyToPayChangeResponse } from '@google-pay/button-angular';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BusinessModel, PricingInformation } from '@platform-frontend/marketplace';
import { YattaLangId } from '@platform-frontend/transloco';
import { ShowErrorNotification, TypedChanges } from '@platform-frontend/util';
import { map, merge, Observable, Subject } from 'rxjs';
import { MSG_WALLET_PAYMENT_GOOGLE_ACCOUNT_ERROR, MSG_WALLET_PAYMENT_GOOGLE_DEFAULT } from '../../../notification-messages';
import { GOOGLE_PAY_API_VERSION, GOOGLE_PAY_MERCHANT_INFO, GOOGLE_PAY_PAYMENT_METHOD_SPECIFICATION } from '../../google-pay-config';
import { WalletPriceInlineItemsService } from '../../wallet-price-inline-items.service';
import { WalletButtonEnvironment } from '../model/wallet-button-environment';
import { WalletButtonTextMode } from '../model/wallet-button-text-mode';

@UntilDestroy()
@Component({
  selector: 'yat-google-pay-button',
  templateUrl: './google-pay-button.component.html',
  styleUrls: ['./google-pay-button.component.scss']
})
export class GooglePayButtonComponent implements OnChanges, OnInit {
  constructor(private store: Store, private translocoService: TranslocoService, private walletPriceInlineItemsService: WalletPriceInlineItemsService) {}

  @Input()
  buttonText: WalletButtonTextMode = 'BUY';

  @Input()
  buttonLanguage: YattaLangId;

  @Input()
  debitDate: Date | null;

  @Input()
  pricingInformation: PricingInformation;

  @Input()
  walletButtonEnvironment: WalletButtonEnvironment = 'LIVE';

  @Input()
  vendorName: string = '';

  @Input()
  businessModel: BusinessModel = 'B2C';

  @Output()
  loadPaymentData: EventEmitter<google.payments.api.PaymentData> = new EventEmitter<google.payments.api.PaymentData>();

  googlePayEnvironment: google.payments.api.Environment;
  buttonType: google.payments.api.ButtonType;
  displayItems$: Observable<google.payments.api.DisplayItem[]>;

  private pricingOrBusinessModelChanged$: Subject<void> = new Subject();

  ngOnInit(): void {
    this.displayItems$ = merge(this.pricingOrBusinessModelChanged$, this.translocoService.langChanges$).pipe(
      map(() => this.walletPriceInlineItemsService.createGooglePayLineItems(this.businessModel, this.pricingInformation))
    );
  }

  ngOnChanges(changes: TypedChanges<GooglePayButtonComponent>): void {
    if (changes.buttonText) {
      switch (this.buttonText) {
        case 'BUY':
          this.buttonType = 'buy';
          break;
        case 'SUBSCRIBE':
          this.buttonType = 'subscribe';
          break;
        case 'CONTINUE':
          this.buttonType = 'plain';
      }
    }

    if (changes.walletButtonEnvironment) {
      this.googlePayEnvironment = this.walletButtonEnvironment === 'TEST' ? 'TEST' : 'PRODUCTION';
    }

    if (changes.pricingInformation || changes.businessModel) {
      this.pricingOrBusinessModelChanged$.next();
    }
  }

  onReadyToPayChange(event: Event): void {
    const detail = (event as CustomEvent<ReadyToPayChangeResponse>).detail;
    if (!(detail.isButtonVisible && detail.isReadyToPay)) this.store.dispatch(new ShowErrorNotification(MSG_WALLET_PAYMENT_GOOGLE_DEFAULT));
  }

  onLoadPaymentData(event: Event): void {
    this.loadPaymentData.emit((event as CustomEvent<google.payments.api.PaymentData>).detail);
  }

  onError(event: Event): void {
    const error: Error | google.payments.api.PaymentsError = (event as CustomEvent<Error>).detail;
    if ('statusCode' in error && error.statusCode === 'BUYER_ACCOUNT_ERROR') {
      this.store.dispatch(new ShowErrorNotification(MSG_WALLET_PAYMENT_GOOGLE_ACCOUNT_ERROR));
    } else {
      this.store.dispatch(new ShowErrorNotification(MSG_WALLET_PAYMENT_GOOGLE_DEFAULT));
    }
  }

  buildPaymentRequest(transactionInfo: google.payments.api.TransactionInfo): google.payments.api.PaymentDataRequest {
    return {
      ...GOOGLE_PAY_API_VERSION,
      allowedPaymentMethods: GOOGLE_PAY_PAYMENT_METHOD_SPECIFICATION,
      merchantInfo: GOOGLE_PAY_MERCHANT_INFO,
      transactionInfo
    };
  }
}
